import {Command} from './undo-manager';
import {PolygonField} from '../enums/database/polygon-field.enum';
import {PointField} from '../enums/database/point-field.enum';

/**
 * The map's undo history is a list of document changes.
 *
 * Every step, from moving one vertex to clearing the whole map, is recorded as
 * the before and after state of the Firestore documents it touched: a shape
 * (its vertices live inside it), a group or a map layer. Recording documents
 * rather than "what the user did" is what lets one mechanism undo everything
 * the same way, and lets an undo be checked against what the map holds now --
 * someone else may have changed the same shape since.
 */

export type MapDocKind = 'geo' | 'group' | 'layer';

/** One document, before and after. Null means the document did not exist. */
export interface DocChange {
  kind: MapDocKind;
  id: string;
  before: any | null;
  after: any | null;
}

/** String keys naming a step, for "Undone: …". */
export const UndoLabel = {
  EDIT: 'undo_label_edit',
  DRAW: 'undo_label_draw',
  GROUP: 'undo_label_group',
  DELETE: 'undo_label_delete',
  SPLIT: 'undo_label_split',
  JOIN: 'undo_label_join',
  SUBTRACT: 'undo_label_subtract',
  IMPORT: 'undo_label_import',
  CLEAR: 'undo_label_clear',
  RESTORE: 'undo_label_restore',
} as const;
export type UndoLabel = typeof UndoLabel[keyof typeof UndoLabel];

/**
 * When one gesture records several kinds of change -- an import that clears
 * first, a split that deletes one shape and draws two -- the step is named
 * after the biggest of them.
 */
const LABEL_WEIGHT: {[label: string]: number} = {
  [UndoLabel.EDIT]: 0,
  [UndoLabel.DRAW]: 1,
  [UndoLabel.GROUP]: 2,
  [UndoLabel.DELETE]: 3,
  [UndoLabel.SPLIT]: 4,
  [UndoLabel.JOIN]: 4,
  [UndoLabel.SUBTRACT]: 4,
  [UndoLabel.IMPORT]: 5,
  [UndoLabel.CLEAR]: 6,
  [UndoLabel.RESTORE]: 7,
};

export function heavierLabel(a: UndoLabel | null, b: UndoLabel): UndoLabel {
  return !a || LABEL_WEIGHT[b] > LABEL_WEIGHT[a] ? b : a;
}

/**
 * Steps worth announcing. Moving a vertex back is its own feedback; a whole map
 * coming back, or a shape reappearing, is worth a word.
 */
export const ANNOUNCED_LABELS: string[] = [UndoLabel.DELETE, UndoLabel.SPLIT, UndoLabel.JOIN, UndoLabel.SUBTRACT,
  UndoLabel.GROUP, UndoLabel.IMPORT, UndoLabel.CLEAR, UndoLabel.RESTORE];

export interface MapCommand extends Command {
  label: UndoLabel;
  changes: DocChange[];
  /**
   * When the command last took effect (recorded, undone or redone). A document
   * someone else changed after this is not deleted by the next undo or redo.
   */
  appliedAt: number;
  /** Changes left alone the last time it ran because the map had moved on. */
  conflicts: number;
  /**
   * Comments the step removed (Clear Map). They are not map documents -- the
   * API writes them -- so undo and redo put them back and take them away
   * through it.
   */
  comments?: {projectId: string, ids: string[]};
}

/** Written on every save to say who saved; not part of what a shape is. */
const BOOKKEEPING_KEYS = new Set<string>([
  PolygonField.KEY_LAST_UPDATE_USER,
  PolygonField.KEY_LAST_UPDATE_DEVICE,
  PolygonField.KEY_LAST_UPDATE_TIMESTAMP,
  PolygonField.KEY_LAST_UPDATE_SESSION_ID,
]);

/**
 * Worked out from the shape rather than chosen by anyone: area, perimeter, the
 * vertex count and numbering, and a vertex's height above sea level (looked up
 * from its position). They follow whatever the undo puts back, and a
 * difference in them is never a reason to leave a change alone. The count and
 * numbering are then made right for the outline the merge leaves
 * (withMergedOutline).
 */
const DERIVED_KEYS = new Set<string>([
  PolygonField.KEY_TOTAL_AREA,
  PolygonField.KEY_TOTAL_PERIMETER,
  PolygonField.KEY_NUM_POINTS,
  PointField.KEY_INDEX,
  PointField.KEY_ALT,
]);

/** Where a stored shape keeps its vertices, by id. */
const POINTS_FIELD = 'points';

/**
 * Where a vertex sits in its shape's outline: its number and its neighbours.
 * They are not merged vertex by vertex but rebuilt for the whole outline once
 * everything else is (withMergedOutline). Taken back one vertex at a time, the
 * numbers put a vertex someone else had inserted meanwhile on the wrong side
 * of its neighbour.
 */
const OUTLINE_KEYS = new Set<string>([
  PointField.KEY_INDEX,
  PointField.KEY_PREV_POINT_ID,
  PointField.KEY_NEXT_POINT_ID,
]);

/** What a vertex's removal passes over: who saved it, what follows from it, where it sits. */
const REMOVAL_IGNORED_KEYS = new Set<string>([...BOOKKEEPING_KEYS, ...DERIVED_KEYS, ...OUTLINE_KEYS]);

/** A deep copy of a stored document; absent stays null. */
export function cloneDoc<T>(doc: T): T {
  return doc === null || doc === undefined ? null as unknown as T : JSON.parse(JSON.stringify(doc));
}

/**
 * A copy without the last-update stamps.
 *
 * Shallow on purpose: a clear of thousands of shapes records every one, and a
 * deep copy each time made that slow. It is safe because stored documents are
 * never changed in place -- a save replaces the cached document with a new
 * one, and a merge builds new objects -- so history can share their insides.
 */
export function withoutBookkeeping(doc: any): any {
  if (!doc) {
    return null;
  }
  const copy = {...doc};
  BOOKKEEPING_KEYS.forEach(key => delete copy[key]);
  return copy;
}

function isPlainObject(value: any): boolean {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Structural equality for stored documents: key order does not matter, an
 * absent key equals undefined and null, and the last-update stamps are ignored.
 */
export function docsEqual(a: any, b: any): boolean {
  return equalExcept(a, b, BOOKKEEPING_KEYS);
}

const BOOKKEEPING_AND_DERIVED_KEYS = new Set<string>([...BOOKKEEPING_KEYS, ...DERIVED_KEYS]);

/**
 * docsEqual that also passes over the derived values (DERIVED_KEYS) at any
 * depth, a vertex's height included: another session looking up and saving
 * the corners' heights has not changed the shape anyone drew.
 */
export function docsEqualIgnoringDerived(a: any, b: any): boolean {
  return equalExcept(a, b, BOOKKEEPING_AND_DERIVED_KEYS);
}

function equalExcept(a: any, b: any, ignored: Set<string>): boolean {
  if (a === b) {
    return true;
  }
  if (a === null || a === undefined || b === null || b === undefined) {
    return (a === null || a === undefined) && (b === null || b === undefined);
  }
  if (typeof a !== typeof b) {
    return false;
  }
  if (typeof a === 'number') {
    return Number.isNaN(a) && Number.isNaN(b);
  }
  if (typeof a !== 'object') {
    return false;
  }
  if (Array.isArray(a) !== Array.isArray(b)) {
    return false;
  }
  if (Array.isArray(a)) {
    return a.length === b.length && a.every((value, i) => equalExcept(value, b[i], ignored));
  }
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (!ignored.has(key) && !equalExcept(a[key], b[key], ignored)) {
      return false;
    }
  }
  return true;
}

/**
 * Takes a change back (or does it again) on top of what the document holds now.
 *
 * `from` is what the step left, `to` what it should become, `current` what is
 * there now. Only the fields the step itself changed are touched, and only
 * where they still hold what the step left: rename a shape, attach a photo,
 * undo the rename, and the photo stays. A field someone has changed since is
 * left as it is and counted as a conflict. Vertices are compared one by one,
 * so moving one corner back leaves a corner someone else moved where it is.
 * Which vertices there are and the order they go in is settled for the whole
 * outline (withMergedOutline), so taking out a vertex while someone else has
 * put one in keeps theirs where they put it.
 */
export function mergeUndo(current: any, from: any, to: any): {value: any, conflicts: number} {
  return mergeValue(current, from, to, '', true);
}

function mergeValue(current: any, from: any, to: any, key: string, isDoc: boolean = false): {value: any, conflicts: number} {
  if (docsEqual(from, to)) {
    return {value: current, conflicts: 0};
  }
  if (docsEqual(current, from)) {
    return {value: cloneDoc(to), conflicts: 0};
  }
  // Taking out a vertex someone's session has since given a height to, or
  // renumbered by putting in or taking out another: the height was worked out
  // from it, the number and neighbours are rebuilt below, and neither is a
  // reason to keep it.
  if ((to === null || to === undefined) && equalExcept(current, from, REMOVAL_IGNORED_KEYS)) {
    return {value: cloneDoc(to), conflicts: 0};
  }
  if (isPlainObject(current) && isPlainObject(from) && isPlainObject(to)) {
    const out = {...current};
    let conflicts = 0;
    const keys = new Set([...Object.keys(from), ...Object.keys(to)]);
    for (const k of keys) {
      if (BOOKKEEPING_KEYS.has(k)) {
        continue;
      }
      const merged = mergeValue(current[k], from[k], to[k], k);
      // cloneDoc turns "absent" into null; a key the target never had goes.
      if (merged.value === undefined || (merged.value === null && to[k] === undefined)) {
        delete out[k];
      } else {
        out[k] = merged.value;
      }
      conflicts += merged.conflicts;
    }
    if (isDoc && outlineChanged(from[POINTS_FIELD], to[POINTS_FIELD])) {
      withMergedOutline(out, current, from, to);
    }
    return {value: out, conflicts};
  }
  if (DERIVED_KEYS.has(key) || OUTLINE_KEYS.has(key)) {
    return {value: cloneDoc(to), conflicts: 0};
  }
  return {value: current, conflicts: 1};
}

/** Whether a step changed which vertices a shape has, or their order. */
function outlineChanged(from: any, to: any): boolean {
  if (!isPlainObject(from) || !isPlainObject(to)) {
    return false;
  }
  const ids = new Set([...Object.keys(from), ...Object.keys(to)]);
  for (const id of ids) {
    if (!isPlainObject(from[id]) || !isPlainObject(to[id])) {
      return true;
    }
    for (const key of OUTLINE_KEYS) {
      if (from[id][key] !== to[id][key]) {
        return true;
      }
    }
  }
  return false;
}

/**
 * A stored shape's vertex ids in outline order: by number, a tie in the order
 * they are stored, as the map loads them.
 */
function outlineOrder(points: any): string[] {
  if (!isPlainObject(points)) {
    return [];
  }
  const position = (id: string): number => {
    const index = points[id][PointField.KEY_INDEX];
    return typeof index === 'number' && !Number.isNaN(index) ? index : Number.POSITIVE_INFINITY;
  };
  return Object.keys(points)
    .filter(id => isPlainObject(points[id]))
    .sort((a, b) => position(a) < position(b) ? -1 : position(a) > position(b) ? 1 : 0);
}

/** Whether the vertices both outlines have go in the same order in each. */
function sameRelativeOrder(a: string[], b: string[]): boolean {
  const inA = new Set(a);
  const inB = new Set(b);
  const common = b.filter(id => inA.has(id));
  return a.filter(id => inB.has(id)).every((id, i) => id === common[i]);
}

/**
 * `base`, less the vertices not kept, with the kept ones only `other` has put
 * in, each beside its neighbour there: after the nearest one before it that is
 * already in, or else before the nearest one after it. A vertex first or last
 * in `other` goes first or last, so a line's ends and a closed shape's closing
 * copy stay where they belong.
 */
function weave(base: string[], other: string[], keep: Set<string>): string[] {
  const order = base.filter(id => keep.has(id));
  const placed = new Set(order);
  other.forEach((id, i) => {
    if (!keep.has(id) || placed.has(id)) {
      return;
    }
    let at = i === other.length - 1 ? order.length : i === 0 ? 0 : -1;
    for (let j = i - 1; at < 0 && j >= 0; j--) {
      if (placed.has(other[j])) {
        at = order.indexOf(other[j]) + 1;
      }
    }
    for (let j = i + 1; at < 0 && j < other.length; j++) {
      if (placed.has(other[j])) {
        at = order.indexOf(other[j]);
      }
    }
    order.splice(at < 0 ? order.length : at, 0, id);
    placed.add(id);
  });
  keep.forEach(id => {
    if (!placed.has(id)) {
      order.push(id);
    }
  });
  return order;
}

/**
 * Puts the vertices of a merged shape (`doc`, changed in place: it is the
 * merge's own copy) in one outline order, numbered and linked to match, with
 * the count beside them.
 *
 * A vertex's number is its place in the outline, so numbers cannot be merged
 * one vertex at a time. Undo an inserted vertex after someone else has
 * inserted one further along, and the old numbers came back for every vertex
 * but theirs: theirs then shared a number with a neighbour, the sort put it on
 * whichever side it happened to, and the outline grew a spike or a crossing
 * that was saved with the old vertex count.
 *
 * So the outline as it is now is kept, less the vertices the step takes out,
 * and each vertex it brings back goes in beside its neighbour in the outline
 * it restores. Only a step that put the vertices it kept in another order --
 * nothing in the editor does -- has its own order kept instead, with anything
 * added since beside its neighbour now.
 */
function withMergedOutline(doc: any, current: any, from: any, to: any): void {
  const points = doc[POINTS_FIELD];
  if (!isPlainObject(points)) {
    return;
  }
  const keep = new Set(Object.keys(points).filter(id => isPlainObject(points[id])));
  const now = outlineOrder(current[POINTS_FIELD]);
  const target = outlineOrder(to[POINTS_FIELD]);
  const order = sameRelativeOrder(outlineOrder(from[POINTS_FIELD]), target)
    ? weave(now, target, keep)
    : weave(target, now, keep);
  const renumbered = {...points};
  order.forEach((id, index) => {
    const vertex = points[id];
    const prev = order[(index + order.length - 1) % order.length];
    const next = order[(index + 1) % order.length];
    if (vertex[PointField.KEY_INDEX] !== index || vertex[PointField.KEY_PREV_POINT_ID] !== prev
      || vertex[PointField.KEY_NEXT_POINT_ID] !== next) {
      // A new object: the vertex may be shared with what the map or the
      // history holds (withoutBookkeeping).
      renumbered[id] = {...vertex, [PointField.KEY_INDEX]: index,
        [PointField.KEY_PREV_POINT_ID]: prev, [PointField.KEY_NEXT_POINT_ID]: next};
    }
  });
  doc[POINTS_FIELD] = renumbered;
  if (doc[PolygonField.KEY_NUM_POINTS] !== undefined || to[PolygonField.KEY_NUM_POINTS] !== undefined) {
    doc[PolygonField.KEY_NUM_POINTS] = order.length;
  }
}

/**
 * Gathers changes into one step: per document, the state before the first
 * change and after the last. A shape drawn and deleted inside one step never
 * existed as far as the history is concerned.
 */
export function mergeInto(target: Map<string, DocChange>, changes: DocChange[]) {
  for (const change of changes) {
    const key = `${change.kind}:${change.id}`;
    const existing = target.get(key);
    if (existing) {
      existing.after = change.after;
    } else {
      target.set(key, {...change});
    }
  }
}

/** The changes that actually change something. */
export function effectiveChanges(changes: Iterable<DocChange>): DocChange[] {
  return Array.from(changes).filter(c => !docsEqual(c.before, c.after));
}
